import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styles from '../PlantDesignDetails.module.scss';
import { FormGroup, Input } from '@snsw/react-component-library';
import { isEmpty, isNumeric, validateExt } from 'shared/utils/Validation';
import { mapRefDataPickList } from 'shared/utils/Helpers';
import { PlantDesignRefData } from 'plantDesign/constants/PlantDesignRefData';
import { PlantDesignDetailsPage } from 'plantDesign/constants/plantDesignDetailsPage';
import { RadioOrDropdownSelector } from 'shared/components/selector/RadioOrDropdownSelector';

export const HoistWithPlatformMovementDesignDetails = React.forwardRef((props, ref) => {
  const {
    hoistType: savedHoistType = '',
    otherHoistType: savedOtherHoistType = '',
    maxRatedCapacity: savedMaxRatedCapacity = '',
    maxFreestandingHeight: savedMaxFreestandingHeight = '',
    maxTravel: savedTravel = '',
    maxVerticalTravellingSpeed: savedMaxVerticalTravellingSpeed = '',
  } = props.value || {};

  const [hoistType, setHoistType] = useState(savedHoistType);
  const [otherHoistType, setOtherHoistType] = useState(savedOtherHoistType);
  const [maxRatedCapacity, setMaxRatedCapacity] = useState(savedMaxRatedCapacity);
  const [maxTravel, setTravel] = useState(savedTravel);
  const [maxFreestandingHeight, setMaxFreestandingHeight] = useState(savedMaxFreestandingHeight);
  const [maxVerticalTravellingSpeed, setMaxVerticalTravellingSpeed] = useState(savedMaxVerticalTravellingSpeed);

  const [showErrors, setShowErrors] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorSummary, setErrorSummary] = useState([]);

  const REF_CONSTANTS = PlantDesignRefData.hoistWithPlatformMovement;
  const PAGE_CONSTANTS = PlantDesignDetailsPage.hoistWithPlatformMovement;

  const hoistTypeOptions = mapRefDataPickList(props.refData[REF_CONSTANTS.hoistTypeOptions]);

  const validators = [
    {
      fieldName: 'hoistType',
      fieldValue: hoistType,
      isValid: (v) => !isEmpty(v),
      errorMessage: () => 'Please select an option',
    },
    {
      fieldName: 'otherHoistType',
      fieldValue: otherHoistType,
      isValid: (v) => hoistType !== REF_CONSTANTS.otherHoistType || !isEmpty(v),
      errorMessage: () => 'Please enter details',
    },
    {
      fieldName: 'maxRatedCapacity',
      fieldValue: maxRatedCapacity,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxFreestandingHeight',
      fieldValue: maxFreestandingHeight,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxTravel',
      fieldValue: maxTravel,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
    {
      fieldName: 'maxVerticalTravellingSpeed',
      fieldValue: maxVerticalTravellingSpeed,
      isValid: isNumeric,
      errorMessage: (v) => (isEmpty(v) ? 'Please enter details' : 'Please enter only numeric value'),
    },
  ];

  if (ref) {
    ref.current = {
      validate: () => {
        const isValid = validateExt(validators, setErrors, setErrorSummary);
        setShowErrors(!isValid);
        return isValid;
      },
    };
  }

  useEffect(() => {
    const details = {
      hoistType,
      otherHoistType,
      maxRatedCapacity,
      maxTravel,
      maxFreestandingHeight,
      maxVerticalTravellingSpeed,
    };

    if (hoistType !== REF_CONSTANTS.otherHoistType) delete details.otherHoistType;

    props.onChange(details);

    if (showErrors) validateExt(validators, setErrors, setErrorSummary);
  }, [hoistType, otherHoistType, maxRatedCapacity, maxTravel, maxFreestandingHeight, maxVerticalTravellingSpeed]);

  useEffect(() => {
    props.onErrorChange(errorSummary);
  }, [errorSummary]);

  return (
    <>
      <RadioOrDropdownSelector
        id="hoistType"
        name="hoistType"
        options={hoistTypeOptions}
        title={PAGE_CONSTANTS.hoistType.label}
        errorMessage={errors?.hoistType?.errorMessage}
        hasError={showErrors && errors?.hoistType?.invalid}
        onChange={(v) => {
          setHoistType(v);
          setOtherHoistType('');
        }}
        value={hoistType}
        showExtraInput={hoistType === REF_CONSTANTS.otherHoistType}
        extraInputType="textarea"
        onChangeExtraInput={setOtherHoistType}
        extraInputValue={otherHoistType}
        extraInputErrorMessage={errors?.otherHoistType?.errorMessage}
        extraInputHasError={showErrors && errors?.otherHoistType?.invalid}
        extraInputTitle={'Please describe the type of hoist'}
        margin={{ top: 'md' }}
        maxOptionsLengthForRadio={5}
      />

      <FormGroup
        id="maxRatedCapacity"
        label={PAGE_CONSTANTS.maxRatedCapacity.label}
        isRequired
        errorMessage={errors?.maxRatedCapacity?.errorMessage}
        hasError={showErrors && errors?.maxRatedCapacity?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'md' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxRatedCapacity.suffix}
          onChange={(e) => setMaxRatedCapacity(e.target.value)}
          value={maxRatedCapacity}
          inputWidth="md"
        />
      </FormGroup>

      <FormGroup
        id="maxFreestandingHeight"
        label={PAGE_CONSTANTS.maxFreestandingHeight.label}
        isRequired
        errorMessage={errors?.maxFreestandingHeight?.errorMessage}
        hasError={showErrors && errors?.maxFreestandingHeight?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'md' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxFreestandingHeight.suffix}
          onChange={(e) => setMaxFreestandingHeight(e.target.value)}
          value={maxFreestandingHeight}
          inputWidth="md"
        />
      </FormGroup>

      <FormGroup
        id="maxTravel"
        label={PAGE_CONSTANTS.maxTravel.label}
        isRequired
        errorMessage={errors?.maxTravel?.errorMessage}
        hasError={showErrors && errors?.maxTravel?.invalid}
        className={styles.inputWithSuffix}
        margin={{ top: 'md' }}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxTravel.suffix}
          onChange={(e) => setTravel(e.target.value)}
          value={maxTravel}
          inputWidth="md"
        />
      </FormGroup>

      <FormGroup
        id="maxVerticalTravellingSpeed"
        label={PAGE_CONSTANTS.maxVerticalTravellingSpeed.label}
        isRequired
        errorMessage={errors?.maxVerticalTravellingSpeed?.errorMessage}
        hasError={showErrors && errors?.maxVerticalTravellingSpeed?.invalid}
        className={styles.inputWithSuffix}
      >
        <Input
          suffix={PAGE_CONSTANTS.maxVerticalTravellingSpeed.suffix}
          onChange={(e) => setMaxVerticalTravellingSpeed(e.target.value)}
          value={maxVerticalTravellingSpeed}
          inputWidth="md"
        />
      </FormGroup>
    </>
  );
});

HoistWithPlatformMovementDesignDetails.displayName = 'HoistWithPlatformMovementDesignDetails';

HoistWithPlatformMovementDesignDetails.propTypes = {
  value: PropTypes.object,
  refData: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  onErrorChange: PropTypes.func.isRequired,
};
